import {
  filterValidValue,
  getArrayVal,
} from '@vindr/utils/helpers/common.helper';
import { ALLTIME_STUDYDATE } from '../constants/study.constant';
import { Institution } from '../interfaces';
import { getDetailInstitution } from './institution.api';
import {
  ParamsGetOrders,
  ResponseGetOrderWorklist,
  ResponseGetOrders,
  getOrders,
} from './order.api';
import {
  LatestReportByOrderResponse,
  ResponseApprover,
  getApprover,
  getLatestReportByOrder,
  getReportByVersionId,
} from './report.api';
import { ResponseFetchStudyList, getDetailStudy } from './study.api';

type SelectedRowKey =
  | ResponseFetchStudyList['data']
  | ResponseGetOrderWorklist['data'];
type SelectedRow = SelectedRowKey[number];
type RecentOrder = ResponseGetOrders['data'][number];
type RecentReport = LatestReportByOrderResponse['data'];
export interface PrintReport {
  recentOrder: RecentOrder;
  recentReport: RecentReport;
  study?: ResponseFetchStudyList['data'][number];
  institutionDetail: Institution;
  approver: ResponseApprover['data'] | void;
}

class PrintDataError extends Error {
  public readonly timestamp: string;
  public readonly context: Record<string, unknown>;

  constructor(message: string, context?: Record<string, unknown>) {
    super(message);
    this.name = 'PrintDataError';
    this.timestamp = new Date().toISOString();
    this.context = context || {};

    // Log error details immediately upon creation
    this.logError();
  }

  private logError(): void {
    console.error({
      name: this.name,
      message: this.message,
      timestamp: this.timestamp,
      context: this.context,
      stack: this.stack,
    });
  }

  // Helper method to stringify error for display/logging
  public toString(): string {
    return JSON.stringify(
      {
        name: this.name,
        message: this.message,
        timestamp: this.timestamp,
        context: this.context,
      },
      null,
      2
    );
  }
}

const buildParams = (row: SelectedRow): ParamsGetOrders => {
  const params = filterValidValue<ParamsGetOrders>(
    {
      order_group: getArrayVal(row?.order_group),
      patient_id: row?.patient_id,
      patient_name: row?.patient_name,
      study_id: row?.study_id,
      visit_id: row?.visit_id,
    },
    { invalidList: [null, undefined, ''] }
  );

  if (!Object.keys(params).length) {
    throw new PrintDataError('Invalid parameters: No valid fields found', {
      row,
    });
  }

  return params;
};

const fetchRecentOrder = async (
  params: Partial<ParamsGetOrders>
): Promise<RecentOrder> => {
  try {
    const response = await getOrders({
      ...params,
      offset: 0,
      limit: 1,
    });

    const recentOrder = getArrayVal(response?.data);

    if (!recentOrder) {
      throw new PrintDataError('Recent order not found', { recentOrder });
    }

    return recentOrder;
  } catch (error) {
    if (error instanceof PrintDataError) throw error;
    throw new PrintDataError('Failed to fetch recent order', { params, error });
  }
};

interface ConcurrentDetails {
  recentReport: RecentReport;
  institutionDetail: Institution | null;
  study: PrintReport['study'];
}

const fetchConcurrentDetails = async ({
  recentOrder,
  study,
  study_id,
}: {
  recentOrder: RecentOrder;
  study: PrintReport['study'];
  study_id: string;
}): Promise<ConcurrentDetails> => {
  if (!recentOrder.id || !recentOrder.institution_id) {
    throw new PrintDataError('Invalid order details', { recentOrder });
  }

  const studyListParams = {
    study_id,
    ...ALLTIME_STUDYDATE,
  } satisfies Parameters<typeof getDetailStudy>[0];

  try {
    const [reportResult, institutionResult, studyListResult] =
      await Promise.allSettled([
        getLatestReportByOrder(recentOrder?.id).then(
          async (latestReportResponse) => {
            const latestReport = latestReportResponse?.data;
            const reportDetailResponse = await getReportByVersionId(
              latestReport?.report_version?.id
            );
            const reportDetail = reportDetailResponse?.data;
            return Object.assign({}, latestReport, reportDetail);
          }
        ),
        getDetailInstitution(recentOrder?.institution_id),
        (study
          ? new Promise((resolve) => resolve({ data: [study] } as any))
          : getDetailStudy(studyListParams)) as Promise<ResponseFetchStudyList>,
      ]);

    const recentReport =
      reportResult.status === 'fulfilled'
        ? getArrayVal(reportResult.value)
        : null;

    const institutionDetail =
      institutionResult.status === 'fulfilled'
        ? institutionResult.value?.data
        : null;
    const studyDetail =
      studyListResult?.status === 'fulfilled'
        ? getArrayVal(studyListResult?.value?.data)
        : null;

    if (!recentReport) {
      throw new PrintDataError('No report found', {
        orderId: recentOrder.id,
        reportResult,
      });
    }

    return { recentReport, institutionDetail, study: studyDetail };
  } catch (error) {
    console.error(`Error fetching concurrent details:`, error);
    throw error;
  }
};

const fetchApprover = async (
  reportId: string
): Promise<ResponseApprover['data'] | void> => {
  try {
    const response = await getApprover({ id: reportId });
    return response?.data;
  } catch (error) {
    console.error('Failed to fetch approver:', error);
    return undefined; //? Ignore break when no approver
  }
};

export const getPrintData = async ({
  row,
  params,
}: {
  row: SelectedRow;
  params?: Partial<PrintReport>;
}): Promise<PrintReport> => {
  if (!row) {
    throw new PrintDataError('Invalid row data', { row });
  }

  try {
    const param = buildParams(row);
    const recentOrder = await fetchRecentOrder(param);

    const { recentReport, institutionDetail, study } =
      await fetchConcurrentDetails({
        recentOrder,
        study: params?.study,
        study_id: row?.study_id,
      });

    //? Optional approver fetch - doesn't block if it fails
    /*     const approver = recentReport?.id
      ? await fetchApprover(recentReport.id)
      : undefined; */

    return {
      recentOrder,
      recentReport,
      approver: recentReport?.report_version?.approver,
      institutionDetail,
      ...params,
      study,
    };
  } catch (error) {
    if (error instanceof PrintDataError) throw error;
    throw new PrintDataError(
      `Failed to get print data for study ID: ${row?.study_id}`,
      error
    );
  }
};
